import type { TableColumn, TableRow } from '@data/components/basic/Table/types';

export type ProficiencyLevel = 'LEARNING' | 'PRACTICING' | 'PROFICIENT';

/** Lowest first: ties for the most students resolve to the earlier level. */
export const PROFICIENCY_LEVELS: {
  level: ProficiencyLevel;
  label: string;
  range: string;
}[] = [
  { level: 'LEARNING', label: 'Learning', range: '0–59%' },
  { level: 'PRACTICING', label: 'Practicing', range: '60–79%' },
  { level: 'PROFICIENT', label: 'Proficient', range: '80–100%' },
];

export const MOST_STUDENTS_KEY = 'MOST_STUDENTS';

export function getProficiencyLabel(level: ProficiencyLevel) {
  return PROFICIENCY_LEVELS.find((p) => p.level === level)?.label ?? level;
}

export function getProficiencyLevelByLabel(label: unknown) {
  return PROFICIENCY_LEVELS.find((p) => p.label === label)?.level;
}

/**
 * The level holding the most students. A tie goes to the lowest of the tied
 * levels. `null` when no level has a student, shown as "—".
 */
export function getMostStudentsLevel(
  counts: Partial<Record<ProficiencyLevel, number | null | undefined>>
): ProficiencyLevel | null {
  let most: ProficiencyLevel | null = null;
  let mostCount = 0;

  PROFICIENCY_LEVELS.forEach(({ level }) => {
    const count = Number(counts[level] ?? 0);
    // Strictly greater, so an equal count later on never displaces a lower level.
    if (count > mostCount) {
      most = level;
      mostCount = count;
    }
  });

  return most;
}

/**
 * Adds a "Most students" column after `afterKey`, holding the level's name so
 * the table, sorting and the CSV all read the same value. The level counts are
 * read from the columns keyed by each {@link ProficiencyLevel}.
 */
export function withMostStudentsColumn(
  columns: TableColumn[],
  rows: TableRow[],
  afterKey: string
): { columns: TableColumn[]; rows: TableRow[] } {
  const levelIndices = PROFICIENCY_LEVELS.map(({ level }) => ({
    level,
    index: columns.findIndex((col) => col.key === level),
  }));
  const anchor = columns.findIndex((col) => col.key === afterKey);
  const insertAt = anchor < 0 ? columns.length : anchor + 1;

  const column: TableColumn = {
    type: 'string',
    label: 'Most students',
    key: MOST_STUDENTS_KEY,
    sortable: false,
  };

  const mostStudentsFor = (row: TableRow) => {
    const counts: Partial<Record<ProficiencyLevel, number | null>> = {};
    levelIndices.forEach(({ level, index }) => {
      if (index >= 0) counts[level] = Number(row[index] ?? 0);
    });
    const level = getMostStudentsLevel(counts);
    return level ? getProficiencyLabel(level) : null;
  };

  return {
    columns: [
      ...columns.slice(0, insertAt),
      column,
      ...columns.slice(insertAt),
    ],
    rows: rows.map((row) => [
      ...row.slice(0, insertAt),
      mostStudentsFor(row),
      ...row.slice(insertAt),
    ]),
  };
}
